import { Typography as MuiTypography } from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { z } from "zod";
import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import SeasonalMeanChartContainer from "../../components/charts/seasonalMeanChart/SeasonalMeanChartContainer";
import DrilldownScatterChartContainer from "../../components/charts/drilldownScatterChart/DrilldownScatterChartContainer";
import { getWaterYearRange } from "../fieldDepthProfile/FieldDepthProfile";
import React from "react";
import { usePostHeight } from "../../hooks/usePostHeight";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);

const { start_date, end_date } = getWaterYearRange();

const PARAMETER_OPTIONS = [
  { label: "Chlorophyll-a", value: "24" },
  { label: "Total Nitrogen", value: "29" },
  { label: "Total Phosphorus", value: "28" },
];

const LOCATION_OPTIONS = [
  { label: "CCR1 – Cherry Creek Res. Station 1", value: "79" },
  { label: "CCR2 – Cherry Creek Res. Station 2", value: "80" },
  { label: "CCR3 – Cherry Creek Res. Station 3", value: "81" },
];

const SEASON_OPTIONS = [
  { label: "Annual", value: "annual" },
  { label: "Summer", value: "summer" },
];

const DEFAULTS = {
  parameterid: 24,
  start_date,
  end_date,
  season: "annual",
  locationIds: LOCATION_OPTIONS.map((loc) => loc.value),
};

const schema = z.object({
  parameterid: z.coerce.number().default(DEFAULTS.parameterid),
  start_date: z.string().default(DEFAULTS.start_date),
  end_date: z.string().default(DEFAULTS.end_date),
  season: z
    .enum(SEASON_OPTIONS.map((season) => season.value))
    .default(DEFAULTS.season),
  locationIds: z
    .preprocess((v) => {
      if (Array.isArray(v)) return v;
      if (typeof v === "string") return v.split(",");
      return [];
    }, z.array(z.string()))
    .default(DEFAULTS.locationIds),
  controls: z.preprocess(
    (val) => val === "true" || val === true,
    z.boolean().default(false)
  ),
  parameterControls: z.preprocess(
    (val) => val === "true" || val === true,
    z.boolean().default(true)
  ),
  drilldownControls: z.preprocess(
    (val) => val === "true" || val === true,
    z.boolean().default(true)
  ),
  height: z.preprocess(
    (val) => (typeof val === "string" ? parseInt(val, 10) : val),
    z.number().default(400)
  ),
});

export default function WaterQualityTrendsEmbed() {
  usePostHeight();
  const { params, setParams } = useQueryParams(schema);

  const getParameterLabel = (parameterid) => {
    const param = PARAMETER_OPTIONS.find(
      (p) => p.value === String(parameterid)
    );
    return param ? param.label : "Water Quality Parameter";
  };

  const PARAM_FILTERS = [
    {
      type: "select",
      param: "parameterid",
      label: "Parameter",
      default: String(params.parameterid),
      options: PARAMETER_OPTIONS,
    },
  ];

  const DATE_FILTERS = [
    {
      type: "date",
      param: "start_date",
      label: "Start Date",
      default: params.start_date,
    },
    {
      type: "date",
      param: "end_date",
      label: "End Date",
      default: params.end_date,
    },
    {
      type: "select",
      param: "season",
      label: "Season",
      default: params.season,
      options: SEASON_OPTIONS,
    },
  ];

  return (
    <>
      <main>
        {params.controls && params.parameterControls && (
          <Section style={{ border: "3px solid #c0d0f9" }}>
            <Typography variant="h5" component="h2">
              Parameter Filter
            </Typography>
            <Typography
              variant="body2"
              style={{ marginBottom: 12, fontStyle: "italic" }}
            >
              Selected parameter updates <strong>both</strong> graphs below.
            </Typography>
            <FiltersForm config={PARAM_FILTERS} onChange={setParams} />
          </Section>
        )}

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Seasonal Mean {getParameterLabel(params.parameterid)}
          </Typography>
          <SeasonalMeanChartContainer
            parameterid={params.parameterid}
            endYear={new Date().getFullYear()}
            height={params.height}
          />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            {getParameterLabel(params.parameterid)} Drill-down by Location
          </Typography>
          {params.controls && params.drilldownControls && (
            <Section style={{ marginTop: "16px" }}>
              <Typography variant="h6" component="h3" gutterBottom>
                Date Range & Season Filters
              </Typography>
              <FiltersForm config={DATE_FILTERS} onChange={setParams} />
            </Section>
          )}
          <DrilldownScatterChartContainer
            parameterid={params.parameterid}
            startDate={params.start_date}
            endDate={params.end_date}
            season={params.season}
            locationIds={params.locationIds}
            height={params.height}
          />
        </Section>
      </main>
    </>
  );
}
